import type { ResumeDto, UpdateResumeDto } from "@reactive-resume/dto";
import { useMutation } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import debounce from "lodash.debounce";

import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";
import { useNotificationStore } from "@/client/stores/useNotification";

export const updateResume = async (data: UpdateResumeDto) => {
  try {
    const response = await axios.patch<ResumeDto, AxiosResponse<ResumeDto>, UpdateResumeDto>(
      `/resume/${data.id}`,
      data,
    );

    queryClient.setQueryData<ResumeDto>(["resume", { id: response.data.id }], response.data);

    queryClient.setQueryData<ResumeDto[]>(["resumes"], (cache) => {
      if (!cache) return [response.data];
      return cache.map((resume) => {
        if (resume.id === response.data.id) return response.data;
        return resume;
      });
    });

    return response.data;
  } catch (err: any) {
    // update notification store with the error message
    useNotificationStore.getState().showError(err?.message || "An error occurred");
    throw err;
  }
};

export const debouncedUpdateResume = debounce(updateResume, 500);

export const useUpdateResume = () => {
  const {
    error,
    isPending: loading,
    mutateAsync: updateResumeFn,
  } = useMutation({
    mutationFn: updateResume,
  });

  return { updateResume: updateResumeFn, loading, error };
};
